import { loyaltyEarnRuleIssues, loyaltyEarnRuleObjectSchema, loyaltyPointValueSchema, } from "@station/pricing-engine";
import { z } from "zod";
import { businessUnitTypeSchema } from "../auth/features.js";
import { timestampSchema, uuidSchema } from "../common/primitives.js";
/**
 * Sadakat programı + marka, seviyeler ve kazanım kuralları (ADR 0017 §18–21). Program KİRACI düzeyindedir ve tektir;
 * kazanım kuralı şeması `@station/pricing-engine` `loyaltyEarnRuleSchema`'dır (kasa aynı şemayla "kazanılacak puan"
 * gösterir).
 */
const hexColorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Renk #RRGGBB biçiminde olmalıdır");
const versionSchema = z.number().int().min(1).describe("İyimser kilit sürümü");
/** Tam sayı puan (dizge; `bigint`). */
export const loyaltyPointsSchema = z
    .string()
    .regex(/^\d{1,15}$/, "Puan, negatif olmayan tam sayı olmalıdır")
    .describe("Tam sayı puan (dizge)");
/** İşaretli tam sayı puan (dizge). */
export const loyaltySignedPointsSchema = z
    .string()
    .regex(/^-?\d{1,15}$/, "Puan tam sayı olmalıdır")
    .describe("İşaretli tam sayı puan (dizge)");
const tierMultiplierSchema = z
    .string()
    .regex(/^\d{1,3}(\.\d{1,3})?$/, "Çarpan, en çok 3 ondalık")
    .refine((value) => Number(value) > 0, "Çarpan sıfırdan büyük olmalıdır");
// ── Program ──────────────────────────────────────────────────────────────────
export const loyaltyProgramSchema = z.object({
    id: uuidSchema.nullable(),
    name: z.string(),
    logoFileId: uuidSchema.nullable().describe("`BRANDING_IMAGE` amaçlı yüklenmiş dosya"),
    primaryColor: z.string(),
    secondaryColor: z.string(),
    pointValue: z.string().describe("₺ / puan, 4 ondalık (ör. 0.0100)"),
    pointsExpiryMonths: z.number().int(),
    expiryWarningDays: z.number().int(),
    minRedeemPoints: z.number().int(),
    redeemableBusinessUnits: z.array(businessUnitTypeSchema).describe("Boş = tüm iş birimleri"),
    excludeAgeRestricted: z.boolean(),
    earnEnabled: z.boolean(),
    redeemEnabled: z.boolean(),
    tierWindowMonths: z.number().int(),
    abuseMaxEarnTx: z.number().int(),
    abuseWindowMinutes: z.number().int(),
    abuseDailyPointCap: z.number().int().nullable(),
    isActive: z.boolean(),
    isDefault: z.boolean().describe("Program henüz kaydedilmedi; varsayılanlar gösteriliyor (sürüm 0)"),
    version: z.number().int(),
    updatedAt: timestampSchema.nullable(),
});
export const saveLoyaltyProgramSchema = z.object({
    name: z.string().trim().min(2).max(80),
    logoFileId: uuidSchema.nullable().default(null),
    primaryColor: hexColorSchema.default("#0F766E"),
    secondaryColor: hexColorSchema.default("#F59E0B"),
    pointValue: loyaltyPointValueSchema,
    pointsExpiryMonths: z.number().int().min(1).max(120).default(12),
    expiryWarningDays: z.number().int().min(1).max(365).default(30),
    minRedeemPoints: z.number().int().min(0).max(1_000_000_000).default(100),
    redeemableBusinessUnits: z.array(businessUnitTypeSchema).max(7).default([]),
    excludeAgeRestricted: z.boolean().default(true),
    earnEnabled: z.boolean().default(true),
    redeemEnabled: z.boolean().default(true),
    tierWindowMonths: z.number().int().min(1).max(36).default(12),
    abuseMaxEarnTx: z.number().int().min(1).max(100).default(3),
    abuseWindowMinutes: z.number().int().min(1).max(1440).default(10),
    abuseDailyPointCap: z.number().int().positive().max(2_000_000_000).nullable().default(null),
    isActive: z.boolean().default(true),
    version: z.number().int().min(0).describe("İlk kayıtta 0, sonra okunan sürüm"),
});
// ── Seviye ───────────────────────────────────────────────────────────────────
export const loyaltyTierSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    name: z.string(),
    rank: z.number().int(),
    thresholdPoints: loyaltyPointsSchema.describe("Kayan pencerede kazanılan puan eşiği"),
    earnMultiplier: z.string(),
    color: z.string().nullable(),
    benefits: z.string().nullable(),
    isActive: z.boolean(),
    memberCount: z.number().int().describe("Bu seviyedeki hesap sayısı"),
    version: z.number().int(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const loyaltyTierListSchema = z.object({ items: z.array(loyaltyTierSchema) });
const tierFields = {
    name: z.string().trim().min(2).max(60),
    rank: z.number().int().min(1).max(20).describe("Büyük sıra = üst seviye; etkin seviyeler arasında tekil"),
    thresholdPoints: loyaltyPointsSchema,
    earnMultiplier: tierMultiplierSchema.default("1.000"),
    color: hexColorSchema.nullable().default(null),
    benefits: z.string().trim().max(500).nullable().default(null),
    isActive: z.boolean().default(true),
};
export const createLoyaltyTierSchema = z.object({
    code: z
        .string()
        .trim()
        .regex(/^[A-Z][A-Z0-9_]{1,19}$/, "Kod büyük harf, rakam ve _ (2–20 karakter)")
        .describe("Kampanya motorundaki `LOYALTY_TIER` koşulu bu kodu kullanır; değiştirilemez"),
    ...tierFields,
});
export const updateLoyaltyTierSchema = z.object({
    name: tierFields.name.optional(),
    rank: tierFields.rank.optional(),
    thresholdPoints: tierFields.thresholdPoints.optional(),
    earnMultiplier: tierMultiplierSchema.optional(),
    color: hexColorSchema.nullable().optional(),
    benefits: z.string().trim().max(500).nullable().optional(),
    isActive: z.boolean().optional(),
    version: versionSchema,
});
// ── Kazanım kuralı ───────────────────────────────────────────────────────────
export const loyaltyEarnRuleViewSchema = loyaltyEarnRuleObjectSchema.extend({
    version: z.number().int(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const loyaltyEarnRuleListSchema = z.object({ items: z.array(loyaltyEarnRuleViewSchema) });
export const createLoyaltyEarnRuleSchema = loyaltyEarnRuleObjectSchema
    .omit({ id: true })
    .superRefine((value, context) => {
    for (const issue of loyaltyEarnRuleIssues(value))
        context.addIssue({ code: "custom", path: [issue.path], message: issue.message });
    if (value.validTo && Date.parse(value.validTo) <= Date.parse(value.validFrom))
        context.addIssue({ code: "custom", path: ["validTo"], message: "Bitiş başlangıçtan sonra olmalıdır" });
});
/** Kısmi güncelleme: tür ve kapsam kuralları BİRLEŞMİŞ kayda uygulanır (serviste `loyaltyEarnRuleIssues`). */
export const updateLoyaltyEarnRuleSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    scopeKind: loyaltyEarnRuleObjectSchema.shape.scopeKind.optional(),
    scopeIds: z.array(z.string().min(1)).max(500).optional(),
    pointsPerUnit: z
        .string()
        .regex(/^\d{1,9}(\.\d{1,4})?$/)
        .nullable()
        .optional(),
    multiplier: z
        .string()
        .regex(/^\d{1,3}(\.\d{1,3})?$/)
        .nullable()
        .optional(),
    priority: z.number().int().min(0).max(1000).optional(),
    validFrom: z.iso.datetime({ offset: true }).optional(),
    validTo: z.iso.datetime({ offset: true }).nullable().optional(),
    weekdays: z.array(z.number().int().min(0).max(6)).optional(),
    isActive: z.boolean().optional(),
    version: versionSchema,
});
//# sourceMappingURL=program.js.map